<template>
<div class="input-data-wrapper">
  <template v-if="useNewInterface">
    <div class="nd-header">
      <strong class="nd-header-title">Datas disponíveis</strong>
      <p class="nd-header-subtitle">
        Escolha como deseja buscar sua data para ver as saídas disponíveis.
      </p>
    </div>

    <div class="nd-modos">
      <div
        class="nd-modo-option"
        @click="handleModeChange('proximas')">
        <div
          class="nd-radio-btn"
          :style="searchMode === 'proximas' ? { borderColor: primaryColor } : {}">
          <div
            v-if="searchMode === 'proximas'"
            class="nd-radio-inner"
            :style="{ backgroundColor: primaryColor }" />
        </div>
        <span
          class="nd-modo-label"
          :class="{ 'nd-modo-label--active': searchMode === 'proximas' }">
          Próximas saídas
        </span>
      </div>
      <div
        class="nd-modo-option"
        @click="handleModeChange('por-mes')">
        <div
          class="nd-radio-btn"
          :style="searchMode === 'por-mes' ? { borderColor: primaryColor } : {}">
          <div
            v-if="searchMode === 'por-mes'"
            class="nd-radio-inner"
            :style="{ backgroundColor: primaryColor }" />
        </div>
        <span
          class="nd-modo-label"
          :class="{ 'nd-modo-label--active': searchMode === 'por-mes' }">
          Escolher por mês
        </span>
      </div>
    </div>

    <hr class="nd-divider">

    <!-- Month select (modo por-mês) -->
    <div
      v-if="searchMode === 'por-mes'"
      class="nd-month-select-wrap">
      <i class="fa-regular fa-calendar-days nd-month-icon" />
      <select
        v-model="selectedMonth"
        class="nd-month-select"
        @change="handleMonthChange">
        <option
          v-for="m in availableMonths"
          :key="m.key"
          :value="m.key">
          {{ m.label }}
        </option>
      </select>
      <i class="fas fa-chevron-down nd-month-chevron" />
    </div>

    <!-- Contador -->
    <p
      v-if="!loadingAll"
      class="nd-counter">
      <template v-if="searchMode === 'proximas'">
        {{ filteredDateItems.length }}{{ hasMoreToLoad ? '+' : '' }} saídas encontradas
      </template>
      <template v-else-if="selectedMonth">
        {{ selectedMonthItems.length }} saídas encontradas em {{ selectedMonthLabel }}
      </template>
    </p>

    <!-- Loading all -->
    <div
      v-if="loadingAll"
      class="nd-loading">
      <i class="fa-regular fa-hourglass" />
    </div>

    <!-- Lista de datas -->
    <div
      v-else
      class="nd-list">
      <div
        v-for="item in currentModeItems"
        :key="item.dateStr"
        class="nd-card"
        :class="{
          'nd-card--selected': isSelected(item),
          'nd-card--disabled': item.esgotado,
        }"
        :style="getNewCardStyle(item)"
        @click="onCardClick(item)">
        <div class="nd-card-body">
          <!-- Saída -->
          <div class="nd-dep">
            <span class="nd-label">Saída</span>
            <span class="nd-date">{{ item.depDateCompact }}</span>
            <span class="nd-weekday">{{ item.depWeekdayFull }}</span>
          </div>

          <!-- Connector + Retorno -->
          <template v-if="item.retDay">
            <div class="nd-connector">
              <span
                class="nd-connector-dot"
                :style="{ backgroundColor: isSelected(item) ? primaryColor : '#aaaaaa' }" />
              <div
                class="nd-connector-line"
                :style="{ backgroundColor: isSelected(item) ? primaryColor : '#aaaaaa' }" />
              <i
                class="fas fa-arrow-right nd-connector-arrow"
                :style="{ color: isSelected(item) ? primaryColor : '#aaaaaa' }" />
            </div>
            <div class="nd-ret">
              <span class="nd-label">Retorno</span>
              <span class="nd-date nd-date--ret">{{ item.retDateCompact }}</span>
              <span class="nd-weekday">{{ item.retWeekdayFull }}</span>
            </div>
          </template>

          <!-- Sem retorno -->
          <div
            v-else
            class="nd-spacer">
            <span
              v-if="item.bateVolta"
              class="nd-bate-volta-badge"
              :style="{ backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor }">
              <i class="fas fa-bolt" />&nbsp;Bate-volta
            </span>
          </div>

          <!-- Radio de seleção -->
          <div
            class="nd-radio-sel"
            :style="isSelected(item) ? { borderColor: primaryColor } : {}">
            <div
              v-if="isSelected(item)"
              class="nd-radio-inner-sel"
              :style="{ backgroundColor: primaryColor }" />
          </div>
        </div>

        <!-- Vagas -->
        <div
          v-if="item.esgotado"
          class="nd-vagas-esgotadas">
          <i class="fa-regular fa-user" />&nbsp;Vagas Esgotadas
        </div>
        <div
          v-else
          :style="{ opacity: item.vagasRestantes !== null && item.vagasRestantes <= 5 && !item.esgotado ? 1 : 0 }"
          class="nd-vagas-poucas">
          <i class="fa-regular fa-user" />&nbsp;Apenas {{ item.vagasRestantes }} {{ item.vagasRestantes === 1 ? 'vaga restante' : 'vagas restantes' }}
        </div>
      </div>
    </div>

    <!-- Botão mostrar mais (modo proximas) -->
    <div
      v-if="!loadingAll && !loadingMore && searchMode === 'proximas' && hasMoreProximas"
      class="nd-btn-mais"
      :style="{ borderColor: primaryColor, color: primaryColor }"
      @click="handleShowMore">
      Mostrar mais datas
    </div>

    <div
      v-if="loadingMore && !loadingAll"
      class="nd-loading">
      <i class="fa-regular fa-hourglass" />
    </div>
  </template>
  <template v-else>
    <div class="input-data-lista">
      <!-- Header -->
      <div class="lista-header">
        <div class="lista-header-title">
          <i class="fa-regular fa-calendar-days lista-header-icon" />
          <strong class="lista-header-text">{{ titulo }}</strong>
        </div>
        <span
          v-if="totalDates > 0 && !loadingMore && !hasMoreToLoad"
          class="lista-badge">
          {{ totalDates }} {{ totalDates === 1 ? 'data disponível' : 'datas disponíveis' }}
        </span>
      </div>

      <!-- Date cards -->
      <div
        v-if="visibleDateItems.length > 0"
        class="date-list">
        <div
          v-for="(item, idx) in visibleDateItems"
          :key="idx"
          class="date-card"
          :class="{
            'date-card--selected': isSelected(item),
            'date-card--disabled': item.esgotado,
          }"
          :style="getCardStyle(item)"
          @click="onCardClick(item)">
          <div class="date-card-row">
            <!-- Departure -->
            <div class="date-block">
              <span class="date-day">{{ item.depDay }}</span>
              <span class="date-month">{{ item.depMonth }}</span>
            </div>
            <div
              class="date-weekday-block"
              :class="{ 'date-weekday-block--bate-volta': item.bateVolta }">
              <template v-if="item.bateVolta">
                <span class="date-weekday-full">{{ item.depWeekdayFull }}</span>
                <span class="date-full-date">{{ item.depFullDate }}</span>
              </template>
              <template v-else>
                <span class="date-label-tag">Saída</span>
                <span class="date-weekday-name">{{ item.depWeekday }}</span>
              </template>
            </div>

            <!-- Connector (interval tours only) -->
            <div
              v-if="item.retDay"
              class="date-connector">
              <span class="connector-line" />
              <span
                class="connector-dot"
                :style="{ backgroundColor: primaryColor }" />
              <span class="connector-line" />
            </div>

            <!-- Return (interval tours only) -->
            <template v-if="item.retDay">
              <div class="date-block">
                <span class="date-day">{{ item.retDay }}</span>
                <span class="date-month">{{ item.retMonth }}</span>
              </div>
              <div class="date-weekday-block">
                <span class="date-label-tag">Retorno</span>
                <span class="date-weekday-name">{{ item.retWeekday }}</span>
              </div>
            </template>

            <!-- Spacer or bate-volta badge when no return -->
            <div
              v-else
              class="date-spacer">
              <span
                v-if="item.bateVolta"
                class="bate-volta-badge"
                :style="{ backgroundColor: hexToRgba(primaryColor, 0.12), color: primaryColor }">
                <i class="fas fa-bolt" />&nbsp;Bate-volta
              </span>
            </div>

            <!-- Selection circle -->
            <div
              class="selection-circle"
              :style="isSelected(item) ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}">
              <i
                v-if="isSelected(item)"
                class="fa-solid fa-check selection-check" />
            </div>
          </div>

          <!-- Vagas info -->
          <div
            v-if="item.esgotado"
            class="vagas-row vagas-esgotadas">
            <i class="fa-regular fa-user" />&nbsp;Vagas Esgotadas
          </div>
          <div
            v-else
            :style="{ opacity: item.vagasRestantes !== null && item.vagasRestantes <= 5 && !item.esgotado ? 1 : 0 }"
            class="vagas-row vagas-poucas">
            <i class="fa-regular fa-user" />&nbsp;Apenas {{ item.vagasRestantes }} {{ item.vagasRestantes === 1 ? 'vaga restante' : 'vagas restantes' }}
          </div>
        </div>
      </div>

      <div
        v-else-if="!loadingMore"
        style="text-align: center; font-style: italic;">
        Nenhuma data disponível
      </div>

      <div
        v-if="!loadingMore && (hiddenCount > 0 || hasMoreToLoad)"
        class="toggle-datas"
        :style="{ color: primaryColor }"
        @click="handleToggle">
        <i class="fa-solid fa-chevron-down" />&nbsp;Ver mais {{ (!hasMoreToLoad && hiddenCount > 0) ? hiddenCount + ' ' + (hiddenCount === 1 ? 'data' : 'datas') : 'datas' }}
      </div>

      <div
        v-if="loadingMore"
        style="text-align: center; margin-top: 10px;">
        <i class="fa-regular fa-hourglass" />
      </div>
    </div>
  </template>
</div>
</template>

<script>
import axios from 'axios';

const WEEKDAYS_PT      = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS_PT        = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const WEEKDAYS_FULL_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const MONTHS_FULL_PT   = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const parseDate = function(dateStr) {
  const parts = dateStr.split('-');
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 0, 0, 0, 0);
};

const dateToYMD = function(date) {
  const year  = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day   = String(date.getDate()).padStart(2, '0');
  return year + '-' + month + '-' + day;
};

const parseReturnDays = function(raw) {
  if (typeof raw !== 'object' || Array.isArray(raw) || raw === null) {
    return {};
  }
  return raw;
};

const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);

export default {
  props: {
    value: {
      validator: (v) => v instanceof Date || v == null,
      default:   () => null,
      required:  false,
    },
    availableDays: {
      type:     String,
      default:  () => '[]',
      required: true,
    },
    returnDays: {
      type:     String,
      default:  () => '[]',
      required: true,
    },
    primaryColor: {
      type:     String,
      default:  () => '#FF0000',
      required: true,
    },
    passeioId: {
      type:     String,
      default:  () => '0',
      required: false,
    },
    minDate: {
      type:     Date,
      default:  () => null,
      required: false,
    },
  },
  emits: ['input', 'on-height-change'],
  data() {
    return {
      rawDates:             JSON.parse(this.$props.availableDays) || [],
      availableReturnDates: parseReturnDays(JSON.parse(this.$props.returnDays)),
      visibleCount:         3,
      hasMoreToLoad:        true,
      loadingMore:          false,
      vagasData:            {},
      loadingVagas:         0,
      // nova interface
      searchMode:           'proximas',
      proximasVisibleCount: 5,
      selectedMonth:        null,
      loadingAll:           false,
    };
  },
  computed: {
    hasReturnDates() {
      return Object.keys(this.availableReturnDates).length > 0;
    },
    titulo() {
      return this.hasReturnDates ? 'SELECIONE O PERÍODO' : 'SELECIONE A DATA';
    },
    allDateItems() {
      return this.rawDates.map(raw => this.buildDateItem(raw));
    },
    filteredDateItems() {
      if (!this.minDate) {
        return this.allDateItems;
      }
      return this.allDateItems.filter(item => {
        const d = new Date(item.date);
        d.setHours(23, 59, 59, 0);
        return d >= this.minDate;
      });
    },
    useNewInterface() {
      return this.rawDates.length > 30 || this.hasMoreToLoad;
    },
    availableMonths() {
      const seen   = {};
      const result = [];
      this.filteredDateItems.forEach(item => {
        if (!seen[item.monthKey]) {
          seen[item.monthKey] = true;
          result.push({ key: item.monthKey, label: item.monthLabel });
        }
      });
      return result;
    },
    selectedMonthLabel() {
      const m = this.availableMonths.find(m => m.key === this.selectedMonth);
      return m ? m.label : '';
    },
    selectedMonthItems() {
      if (!this.selectedMonth) {
        return [];
      }
      return this.filteredDateItems.filter(item => item.monthKey === this.selectedMonth);
    },
    proximasVisibleItems() {
      return this.filteredDateItems.slice(0, this.proximasVisibleCount);
    },
    hasMoreProximas() {
      return this.filteredDateItems.length > this.proximasVisibleCount || this.hasMoreToLoad;
    },
    currentModeItems() {
      if (this.searchMode === 'proximas') {
        return this.proximasVisibleItems;
      }
      return this.selectedMonthItems;
    },
    // ----- interface original computeds -----
    visibleDateItems() {
      const items   = this.filteredDateItems;
      const visible = items.slice(0, this.visibleCount);

      if (!this.value) {
        return visible;
      }

      const selectedIdx = items.findIndex(item => this.isSelected(item));

      if (selectedIdx === -1 || selectedIdx < this.visibleCount) {
        return visible;
      }

      return [items[selectedIdx], ...items.slice(0, this.visibleCount - 1)].filter(Boolean);
    },
    hiddenCount() {
      return Math.max(0, this.filteredDateItems.length - this.visibleCount);
    },
    totalDates() {
      return this.filteredDateItems.length;
    },
  },
  async mounted() {
    await this.loadMore();
    this.fetchVagasForVisible();
  },
  methods: {
    buildDateItem(raw) {
      const dateStr = typeof raw === 'string' ? raw : raw.date;

      const date           = parseDate(dateStr);
      const depDay         = date.getDate();
      const depMonth       = MONTHS_PT[date.getMonth()].toUpperCase();
      const depWeekday     = WEEKDAYS_PT[date.getDay()];
      const depWeekdayFull = WEEKDAYS_FULL_PT[date.getDay()];
      const depFullDate    = String(date.getDate()).padStart(2, '0') + ' de ' + MONTHS_FULL_PT[date.getMonth()] + ' de ' + date.getFullYear();
      const depDateCompact = date.getDate() + ' ' + capitalize(MONTHS_FULL_PT[date.getMonth()]);
      const monthKey       = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
      const monthLabel     = capitalize(MONTHS_FULL_PT[date.getMonth()]) + ' ' + date.getFullYear();

      let retDay = null, retMonth = null, retWeekday = null, retWeekdayFull = null, retDateCompact = null, bateVolta = false;
      const retDateStr = this.availableReturnDates[dateStr];
      if (retDateStr) {
        if (retDateStr === dateStr) {
          bateVolta = true;
        } else {
          const retDate  = parseDate(retDateStr);
          retDay         = retDate.getDate();
          retMonth       = MONTHS_PT[retDate.getMonth()].toUpperCase();
          retWeekday     = WEEKDAYS_PT[retDate.getDay()];
          retWeekdayFull = WEEKDAYS_FULL_PT[retDate.getDay()];
          retDateCompact = retDate.getDate() + ' ' + capitalize(MONTHS_FULL_PT[retDate.getMonth()]);
        }
      }

      const vagasInfo      = this.vagasData[dateStr];
      const vagasRestantes = vagasInfo !== undefined ? vagasInfo.vagasRestantes : null;
      const esgotado       = vagasInfo !== undefined ? vagasInfo.esgotado : false;

      return {
        date,
        dateStr,
        depDay,
        depMonth,
        depWeekday,
        depWeekdayFull,
        depFullDate,
        depDateCompact,
        monthKey,
        monthLabel,
        retDay,
        retMonth,
        retWeekday,
        retWeekdayFull,
        retDateCompact,
        bateVolta,
        vagasRestantes,
        esgotado,
      };
    },
    fetchVagasForVisible() {
      const items = this.useNewInterface ? this.currentModeItems : this.visibleDateItems;
      items.forEach(item => {
        this.fetchVagasForDate(item.dateStr);
      });
    },
    async fetchVagasForDate(dateStr) {
      if (this.vagasData[dateStr] !== undefined) {
        return;
      }
      this.$set(this.vagasData, dateStr, { vagasRestantes: null, esgotado: false });
      try {
        const response  = await axios.get('/disponibilidade-horarios/' + this.$props.passeioId + '?quantity=0&day=' + dateStr);
        const schedules = Object.values(response.data || {});
        let maxVagas    = 0;
        schedules.forEach(s => {
          const vagas = parseInt(s.quantidade);
          if (vagas > maxVagas) {
            maxVagas = vagas;
          }
        });
        this.$set(this.vagasData, dateStr, {
          vagasRestantes: maxVagas,
          esgotado:       maxVagas <= 0,
        });
      } finally {
        this.$nextTick(() => this.$emit('on-height-change'));
      }
    },
    isSelected(item) {
      if (!this.value) {
        return false;
      }
      return dateToYMD(this.value) === item.dateStr;
    },
    onCardClick(item) {
      if (!item.esgotado) {
        this.$emit('input', item.date);
      }
    },
    hexToRgba(color, alpha) {
      if (color && color.startsWith('#')) {
        let hex = color.slice(1);
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
          return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
        }
      }
      return 'transparent';
    },
    getCardStyle(item) {
      if (this.isSelected(item)) {
        return {
          borderColor:     this.primaryColor,
          backgroundColor: this.hexToRgba(this.primaryColor, 0.08),
        };
      }
      return {};
    },
    getNewCardStyle(item) {
      if (this.isSelected(item)) {
        return { borderColor: this.primaryColor, };
      }
      return {};
    },
    handleModeChange(mode) {
      if (this.searchMode === mode) {
        return;
      }
      this.searchMode = mode;
      this.$emit('input', null);
      if (mode === 'por-mes') {
        this.loadAll().then(() => {
          this.initSelectedMonth();
          this.$nextTick(() => {
            this.fetchVagasForVisible();
            this.$emit('on-height-change');
          });
        });
      } else {
        this.$nextTick(() => this.$emit('on-height-change'));
      }
    },
    handleMonthChange() {
      this.$emit('input', null);
      this.$nextTick(() => {
        this.fetchVagasForVisible();
        this.$emit('on-height-change');
      });
    },
    initSelectedMonth() {
      const today          = new Date();
      const currentMonthKey = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0');
      const months         = this.availableMonths;
      const current        = months.find(m => m.key === currentMonthKey);
      if (current) {
        this.selectedMonth = current.key;
      } else if (months.length > 0) {
        this.selectedMonth = months[0].key;
      }
    },
    async handleShowMore() {
      if (this.hasMoreToLoad && this.filteredDateItems.length <= this.proximasVisibleCount + 2) {
        await this.loadMore();
      }
      this.proximasVisibleCount += 5;
      this.fetchVagasForVisible();
      this.$nextTick(() => this.$emit('on-height-change'));
    },
    async loadAll() {
      this.loadingAll = true;
      try {
        while (this.hasMoreToLoad) {
          await this.loadMore();
        }
      } finally {
        this.loadingAll = false;
        this.$nextTick(() => this.$emit('on-height-change'));
      }
    },
    // ----- interface original methods -----
    async handleToggle() {
      if (this.hasMoreToLoad && this.filteredDateItems.length <= this.visibleCount + 2) {
        await this.loadMore();
      }
      this.visibleCount += 5;
      this.fetchVagasForVisible();
      this.$nextTick(() => this.$emit('on-height-change'));
    },
    async loadMore() {
      if (this.loadingMore || !this.hasMoreToLoad) {
        return;
      }
      this.loadingMore = true;
      try {
        const lastRaw     = this.rawDates[this.rawDates.length - 1];
        const lastDateStr = lastRaw
          ? (typeof lastRaw === 'string' ? lastRaw : lastRaw.date)
          : null;

        const uri      = lastDateStr ? '?minDate=' + lastDateStr : '';
        const response = await axios.get('/disponibilidade/' + this.$props.passeioId + uri);
        const data     = response.data;
        const dates      = data.availableDays ? data.availableDays : (Array.isArray(data) ? data : []);
        const returnDates = data.availableReturnDays ? data.availableReturnDays : {};
        if (!dates || dates.length === 0) {
          this.hasMoreToLoad = false;
        } else {
          this.rawDates = [...(new Set(this.rawDates.concat(dates)))];
          this.availableReturnDates = Object.assign({}, this.availableReturnDates, returnDates);
          if (dates.length < 5) {
            this.hasMoreToLoad = false;
          }
        }
      } finally {
        this.loadingMore = false;
        this.$nextTick(() => this.$emit('on-height-change'));
      }
    },
  },
};
</script>

<style scoped>
/* ===== NOVA INTERFACE ===== */
.nd-header {
  margin-bottom: 12px;
}

.nd-header-title {
  font-size: 12px;
  font-weight: 500;
  color: #464646;
  display: block;
}

.nd-header-subtitle {
  font-size: 12px;
  color: #808080;
  margin: 2px 0 0;
}

.nd-modos {
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin-bottom: 12px;
}

.nd-modo-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.nd-radio-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #5d5d5d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s;
}

.nd-radio-inner {
  width: 15px;
  height: 15px;
  border-radius: 50%;
}

.nd-modo-label {
  font-size: 12px;
  font-weight: 400;
  color: #464646;
}

.nd-modo-label--active {
  font-weight: 600;
}

.nd-divider {
  border: none;
  border-top: 1px solid #dedede;
  margin: 0 0 12px;
}

.nd-month-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #dedede;
  border-radius: 8px;
  padding: 0 12px;
  height: 44px;
  margin-bottom: 12px;
  background: #fff;
}

.nd-month-icon {
  color: #464646;
  font-size: 14px;
  margin-right: 10px;
  flex-shrink: 0;
}

.nd-month-select {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #464646;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.nd-month-chevron {
  font-size: 12px;
  color: #464646;
  pointer-events: none;
  flex-shrink: 0;
}

.nd-counter {
  font-size: 12px;
  font-weight: 500;
  color: #464646;
  margin: 0 0 10px;
}

.nd-loading {
  text-align: center;
  margin: 12px 0;
  color: #888;
}

.nd-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nd-card {
  border: 1px solid #5d5d5d;
  border-radius: 8px;
  padding: 13px 14px;
  cursor: pointer;
  background-color: #fcfcfc;
  transition: border-color 0.15s;
}

.nd-card:hover:not(.nd-card--disabled) {
  border-color: #999;
}

.nd-card--selected {
  background-color: #fcfcfc;
}

.nd-card--disabled {
  cursor: default;
  opacity: 0.55;
}

.nd-card-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.nd-dep,
.nd-ret {
  display: flex;
  flex-direction: column;
  min-width: 62px;
}

.nd-label {
  font-size: 10px;
  font-weight: 600;
  color: #464646;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.nd-date {
  font-size: 14px;
  font-weight: 600;
  color: #464646;
  line-height: 1.3;
  margin-top: 2px;
}

.nd-date--ret {
  font-weight: 300;
  color: #5d5d5d;
}

.nd-weekday {
  font-size: 12px;
  font-weight: 400;
  color: #5d5d5d;
  line-height: 1.2;
  margin-top: 1px;
}

.nd-connector {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
}

.nd-connector-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background-color 0.15s;
}

.nd-connector-line {
  flex: 1;
  height: 2px;
  border-radius: 0;
  transition: background-color 0.15s;
}

.nd-connector-arrow {
  font-size: 10px;
  flex-shrink: 0;
  transition: color 0.15s;
}

.nd-spacer {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.nd-bate-volta-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.nd-radio-sel {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  border: 2px solid #5d5d5d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
  transition: border-color 0.15s;
}

.nd-radio-inner-sel {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.nd-vagas-esgotadas,
.nd-vagas-poucas {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  color: #e53e3e;
}

.nd-vagas-esgotadas {
  justify-content: center;
  font-weight: 600;
}

.nd-btn-mais {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  border-radius: 8px;
  height: 53px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s;
}

.nd-btn-mais:hover {
  opacity: 0.8;
}

/* .input-data-lista {
} */

.lista-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.lista-header-title {
    display: flex;
    align-items: center;
}

.lista-header-icon {
    margin-right: 8px;
    font-size: 16px;
}

.lista-header-text {
    font-size: 14px;
    letter-spacing: 0.3px;
}

.lista-badge {
    background-color: #f0f0f0;
    color: #888888;
    font-size: 11px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: 20px;
    white-space: nowrap;
}

.date-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.date-card {
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    padding: 16px 18px;
    cursor: pointer;
    background-color: #ffffff;
    transition: border-color 0.15s, background-color 0.15s;
}

.date-card:hover:not(.date-card--disabled) {
    border-color: #b0b0b0;
}

.date-card--disabled {
    cursor: default;
    opacity: 0.55;
}

.date-card-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
}

.date-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 36px;
}

.date-day {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.1;
}

.date-month {
    font-size: 12px;
    font-weight: 600;
    color: #888888;
    text-transform: uppercase;
    margin-top: 1px;
}

.date-weekday-block {
    display: flex;
    flex-direction: column;
    min-width: 46px;
}

.date-weekday-block--bate-volta {
    flex: 1;
    min-width: 0;
}

.date-label-tag {
    font-size: 10px;
    font-weight: 700;
    color: #888888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1;
}

.date-weekday-name {
    font-size: 15px;
    font-weight: 500;
    color: #1a1a1a;
    margin-top: 3px;
}

.date-weekday-full {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.date-full-date {
    font-size: 11px;
    color: #888888;
    margin-top: 2px;
    line-height: 1.3;
}

.date-connector {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;
    padding: 0 6px;
}

.connector-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-block;
}

.connector-line {
    flex: 1;
    height: 2px;
    background-color: #e0e0e0;
}

.date-spacer {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.bate-volta-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    white-space: nowrap;
}

.selection-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: auto;
    transition: background-color 0.15s, border-color 0.15s;
}

.selection-check {
    font-size: 10px;
    color: white;
}

.vagas-row {
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
}

.vagas-poucas {
    color: #e53e3e;
}

.vagas-esgotadas {
    color: #e53e3e;
    justify-content: center;
    font-weight: 600;
    margin-right: 43px;
}

.toggle-datas {
    text-align: center;
    margin-top: 14px;
    font-size: 13px;
    font-weight: 500;
    color: #666666;
    cursor: pointer;
    padding: 8px;
    user-select: none;
}

.toggle-datas:hover {
    color: #333333;
}

@media (max-width: 340px) {
    .date-card {
        padding: 10px 10px;
    }

    .date-card-row {
        gap: 5px;
    }

    .date-block {
        min-width: 28px;
    }

    .date-day {
        font-size: 22px;
    }

    .date-month {
        font-size: 10px;
    }

    .date-weekday-block {
        min-width: 34px;
    }

    .date-label-tag {
        font-size: 8px;
    }

    .date-weekday-name {
        font-size: 12px;
    }

    .date-weekday-full {
        font-size: 12px;
    }

    .date-full-date {
        font-size: 10px;
    }

    .connector-line {
        min-width: 10px;
    }

    .selection-circle {
        width: 20px;
        height: 20px;
    }

    .selection-check {
        font-size: 8px;
    }

    .nd-dep,
    .nd-ret {
        min-width: 50px;
    }

    .nd-date {
        font-size: 12px;
    }

    .nd-card {
        padding: 10px 10px;
    }
}
</style>
